import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'src/hooks/use-translation';

import { useAiOrchestrator } from 'src/ai-orchestrator';
import { usePermissions, RESOURCES } from 'src/hooks/use-permissions';
import { TARIFF_FEATURES, useTariff } from 'src/hooks/use-tariff';
import { TariffFeatureGuard } from 'src/components/tariff';
import { getStorage, setStorage } from 'src/utils/local-storage';

import AiIcon from 'icons/monochrome/ai.svg';

import { ICON, assistantMark, brandGradient, hintIn } from './styles';
import { AI_ORCHESTRATOR_NAMESPACE } from './constants';

// ----------------------------------------------------------------------

const HINT_SEEN_KEY = 'ai-orchestrator-launcher-hint-seen';
const HINT_DURATION = 5000;

const EDGE_OFFSET = 32;

const LauncherButton = styled(IconButton)(({ theme }) => ({
  ...assistantMark(theme, 'xl', true),
  boxShadow: theme.customShadows.primary,
  '&:hover': brandGradient(theme, 'dark'),
}));

const HintChip = styled(Box)(({ theme }) => ({
  ...theme.typography.caption,
  fontWeight: theme.typography.fontWeightSemiBold,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadiusLg * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.customShadows.z8,
  whiteSpace: 'nowrap',
  animation: `${hintIn} 0.35s ease-out`,
}));

/**
 * Floating launcher pinned to the bottom-right corner. Opens the resizable
 * assistant drawer, and steps out of the way (fading down) while it is open.
 */
export const AiAssistantLauncher = () => {
  const { t } = useTranslation(AI_ORCHESTRATOR_NAMESPACE);
  const { isOpen, open } = useAiOrchestrator();
  const { canGet } = usePermissions();
  const { canUseFeature } = useTariff();

  const isLauncherVisible = canGet(RESOURCES.AI_ORCHESTRATOR.MAIN);

  const isTariffLocked = !canUseFeature(TARIFF_FEATURES.AI_ASSISTANT);

  // The hint chip is a first-visit nudge only — once shown, never again.
  const [isHintVisible, setIsHintVisible] = useState(() => !getStorage(HINT_SEEN_KEY));

  useEffect(() => {
    if (!isLauncherVisible || !isHintVisible) return undefined;

    setStorage(HINT_SEEN_KEY, true);
    const timer = setTimeout(() => setIsHintVisible(false), HINT_DURATION);
    return () => clearTimeout(timer);
  }, [isLauncherVisible, isHintVisible]);

  if (!isLauncherVisible) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={(theme) => ({
        position: 'fixed',
        bottom: EDGE_OFFSET,
        right: EDGE_OFFSET,
        zIndex: theme.zIndex.speedDial,
        opacity: isOpen ? 0 : 1,
        transform: isOpen ? 'scale(0.8)' : 'scale(1)',
        pointerEvents: isOpen ? 'none' : 'auto',
        transition: theme.transitions.create(['opacity', 'transform'], {
          duration: theme.transitions.duration.shorter,
        }),
      })}
    >
      {isHintVisible && !isTariffLocked && (
        <HintChip>
          <Typography variant="inherit">{t('launcher-hint')}</Typography>
        </HintChip>
      )}

      <TariffFeatureGuard feature={TARIFF_FEATURES.AI_ASSISTANT}>
        <Tooltip title={t('open-assistant')} placement="left">
          <LauncherButton onClick={open} aria-label={t('open-assistant')}>
            <AiIcon width={ICON.xl} />
          </LauncherButton>
        </Tooltip>
      </TariffFeatureGuard>
    </Stack>
  );
};
